import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import SaveDialog from "../components/SaveDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { useDirtyGuard, useUnsavedChangesWarning } from "../context/DirtyContext";
import { api, formatApiError } from "../lib/api";
import { Calculator, Save, Loader2, Calendar, Sparkles, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import AutoCell from "../components/AutoCell";

/** Row counts per section. */
const SALES_ROWS = 15;
const PURCHASE_ROWS = 15;

const num = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
};

const fmt = (n) => {
    if (!Number.isFinite(n) || n === 0) return "";
    const s = n.toFixed(3);
    return s.replace(/\.?0+$/, "");
};

const fmtNonZero = (n) => (Number.isFinite(n) ? n.toFixed(3).replace(/\.?0+$/, "") : "");

const emptySaleRow = (i) => ({
    idx: i + 1,
    gold_price: "",
    mfg: "",
    desc: "",
    weight: "",
    cash: "",
    bank: "",
    transfer: "",
});

/* Purchases: amount is now USER INPUT (per #78); broken_weight is section-level (per #79). */
const emptyPurchaseRow = (i) => ({
    idx: i + 1,
    weight: "",
    gold_price: "",
    desc: "",
    amount: "",
});

const todayIso = () => new Date().toISOString().slice(0, 10);

/* ────────────────────────── Sales Table ────────────────────────── */
export function SalesTable({ rows, onChange, t, totals, openings, displayName, readOnly = false, onAddRow }) {
    /* Per-row profit (Sales) = ((gold_price + mfg) * weight) - (cash + bank + transfer) */
    const rowProfit = (r) =>
        (num(r.gold_price) + num(r.mfg)) * num(r.weight) -
        (num(r.cash) + num(r.bank) + num(r.transfer));

    return (
        <div className="overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-sm">
            <div className="bg-indigo-700/90 px-4 py-3 text-center text-white">
                <div className="text-sm font-bold sm:text-base" data-testid="adv-sales-section-title">
                    {t.advSalesTitleShort /* البيع */}
                </div>
                {displayName ? (
                    <div className="mt-0.5 text-[11px] font-medium text-white/85 sm:text-xs" data-testid="adv-sales-section-name">
                        {displayName}
                    </div>
                ) : null}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] border-collapse text-center text-xs sm:text-sm">
                    <thead className="bg-indigo-50 text-indigo-900">
                        <tr>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColProfit}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColTransfer}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColBank}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColCash}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColWeight}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColDesc}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColMfg}</th>
                            <th className="border border-indigo-100 px-1 py-2 font-semibold">{t.advColGoldPrice}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Opening balances row (carry-over) — read-only */}
                        {openings && (openings.cash || openings.bank || openings.transfer) ? (
                            <tr className="bg-amber-50/70 text-amber-900">
                                <td className="border border-indigo-100 px-1 py-1 text-[11px] italic" />
                                <td
                                    className="border border-indigo-100 px-1 py-1 font-numbers text-[12px] font-bold"
                                    data-testid="adv-opening-transfer"
                                >
                                    {fmtNonZero(openings.transfer)}
                                </td>
                                <td
                                    className="border border-indigo-100 px-1 py-1 font-numbers text-[12px] font-bold"
                                    data-testid="adv-opening-bank"
                                >
                                    {fmtNonZero(openings.bank)}
                                </td>
                                <td
                                    className="border border-indigo-100 px-1 py-1 font-numbers text-[12px] font-bold"
                                    data-testid="adv-opening-cash"
                                >
                                    {fmtNonZero(openings.cash)}
                                </td>
                                <td className="border border-indigo-100" />
                                <td className="border border-indigo-100 px-1.5 py-1 text-right text-[10px] font-semibold">
                                    <Lock className="me-1 inline h-3 w-3" />
                                    {t.advCarryOverLabel}
                                </td>
                                <td className="border border-indigo-100" />
                                <td className="border border-indigo-100" />
                            </tr>
                        ) : null}

                        {rows.map((r, i) => {
                            const p = rowProfit(r);
                            return (
                                <tr key={i} className="bg-white">
                                    <td className="border border-indigo-100 bg-indigo-50/50 px-1 py-1 font-numbers text-[12px] font-semibold text-indigo-900">
                                        {p ? fmt(p) : ""}
                                    </td>
                                    {["transfer", "bank", "cash", "weight"].map((f) => (
                                        <td key={f} className="border border-indigo-100 align-top p-0">
                                            <AutoCell
                                                value={r[f]}
                                                onChange={(v) => onChange(i, f, v)}
                                                readOnly={readOnly}
                                                numeric
                                                data-testid={`adv-sales-${f}-${i}`}
                                            />
                                        </td>
                                    ))}
                                    <td className="border border-indigo-100 align-top p-0">
                                        <AutoCell
                                            value={r.desc}
                                            onChange={(v) => onChange(i, "desc", v)}
                                            readOnly={readOnly}
                                            data-testid={`adv-sales-desc-${i}`}
                                        />
                                    </td>
                                    {["mfg", "gold_price"].map((f) => (
                                        <td key={f} className="border border-indigo-100 align-top p-0">
                                            <AutoCell
                                                value={r[f]}
                                                onChange={(v) => onChange(i, f, v)}
                                                readOnly={readOnly}
                                                numeric
                                                data-testid={`adv-sales-${f}-${i}`}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                    {totals ? (
                        <tfoot className="font-semibold">
                            <tr className="bg-indigo-100/60 text-indigo-900">
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-total-profit"
                                >
                                    {fmtNonZero(totals.profit)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-total-transfer"
                                >
                                    {fmtNonZero(totals.transfer)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-total-bank"
                                >
                                    {fmtNonZero(totals.bank)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-total-cash"
                                >
                                    {fmtNonZero(totals.cash)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-total-weight"
                                >
                                    {fmtNonZero(totals.weight)}
                                </td>
                                <td className="border border-indigo-200 px-1 py-2 text-right text-[11px] text-indigo-800">
                                    {t.advTotalLabel || t.dailyTotalLabel}
                                </td>
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200" />
                            </tr>
                            {/* Spending (only against cash) */}
                            <tr className="bg-rose-50 text-rose-800">
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200" />
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers"
                                    data-testid="adv-sales-spending"
                                >
                                    {fmtNonZero(totals.spending)}
                                </td>
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200 px-1 py-2 text-right text-[11px] font-semibold">
                                    {t.advSpending}
                                </td>
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200" />
                            </tr>
                            {/* Per-column balances */}
                            <tr className="bg-emerald-50 text-emerald-900">
                                <td className="border border-indigo-200" />
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers font-bold"
                                    data-testid="adv-sales-balance-transfer"
                                >
                                    {fmtNonZero(totals.balance_transfer)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers font-bold"
                                    data-testid="adv-sales-balance-bank"
                                >
                                    {fmtNonZero(totals.balance_bank)}
                                </td>
                                <td
                                    className="border border-indigo-200 px-1 py-2 font-numbers font-bold"
                                    data-testid="adv-sales-balance-cash"
                                >
                                    {fmtNonZero(totals.balance_cash)}
                                </td>
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200 px-1 py-2 text-right text-[11px] font-semibold">
                                    {t.advBalance}
                                </td>
                                <td className="border border-indigo-200" />
                                <td className="border border-indigo-200" />
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
            {!readOnly && onAddRow ? (
                <div className="border-t border-indigo-100 bg-indigo-50/30 px-3 py-2">
                    <button
                        type="button"
                        onClick={onAddRow}
                        className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 bg-white px-3 py-1 text-xs font-semibold text-indigo-800 shadow-sm hover:bg-indigo-100"
                        data-testid="adv-sales-add-row"
                    >
                        + {t.advAddRow}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

/* ─────────────────────── Purchases Table ─────────────────────── */
export function PurchasesTable({
    rows,
    onChange,
    t,
    totals,
    brokenWeight,
    brokenWeightLocked,
    onBrokenWeightChange,
    onBrokenWeightLock,
    displayName,
    readOnly = false,
    onAddRow,
}) {
    /* Per-row profit (Purchases) = (weight * gold_price) - amount (user input) */
    const rowComputed = (r) => num(r.weight) * num(r.gold_price);
    const rowProfit = (r) => rowComputed(r) - num(r.amount);

    return (
        <div className="overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-sm">
            <div className="bg-emerald-700/90 px-4 py-3 text-center text-white">
                <div className="text-sm font-bold sm:text-base" data-testid="adv-pur-section-title">
                    {t.advPurchasesTitleShort /* الشراء */}
                </div>
                {displayName ? (
                    <div className="mt-0.5 text-[11px] font-medium text-white/85 sm:text-xs" data-testid="adv-pur-section-name">
                        {displayName}
                    </div>
                ) : null}
            </div>

            {/* Section-level Broken Weight (single entry, locked after entered or carried) */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-100 bg-emerald-50/40 px-3 py-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900 sm:text-sm">
                    <Lock className={`h-4 w-4 ${brokenWeightLocked ? "text-emerald-700" : "text-emerald-400"}`} />
                    {t.advColBrokenWeight}
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={brokenWeight}
                        onChange={(e) => !readOnly && !brokenWeightLocked && onBrokenWeightChange(e.target.value)}
                        onBlur={() => {
                            if (!readOnly && !brokenWeightLocked && num(brokenWeight) > 0) {
                                onBrokenWeightLock?.();
                            }
                        }}
                        readOnly={readOnly || brokenWeightLocked}
                        inputMode="decimal"
                        placeholder={t.advBrokenWeightPlaceholder}
                        className={`h-9 w-40 rounded-md border bg-white px-2 text-center font-numbers text-sm shadow-sm focus-visible:ring-1 focus-visible:ring-emerald-300 ${
                            brokenWeightLocked ? "cursor-not-allowed bg-emerald-50 text-emerald-900" : ""
                        }`}
                        data-testid="adv-pur-broken-weight"
                    />
                    {brokenWeightLocked ? (
                        <span className="text-[10px] font-semibold text-emerald-700" data-testid="adv-pur-broken-locked">
                            <Lock className="me-1 inline h-3 w-3" />
                            {t.advBrokenWeightLocked}
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse text-center text-xs sm:text-sm">
                    <thead className="bg-emerald-50 text-emerald-900">
                        <tr>
                            <th className="border border-emerald-100 px-1 py-2 font-semibold">{t.advColDescGold}</th>
                            <th className="border border-emerald-100 px-1 py-2 font-semibold">{t.advColProfit}</th>
                            <th className="border border-emerald-100 px-1 py-2 font-semibold">{t.advColAmount}</th>
                            <th className="border border-emerald-100 px-1 py-2 font-semibold">{t.advColGoldPrice}</th>
                            <th className="border border-emerald-100 px-1 py-2 font-semibold">{t.advColWeight}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const p = rowProfit(r);
                            const hasInputs = r.weight || r.gold_price || r.amount;
                            return (
                                <tr key={i} className="bg-white">
                                    <td className="border border-emerald-100 align-top p-0">
                                        <AutoCell
                                            value={r.desc}
                                            onChange={(v) => onChange(i, "desc", v)}
                                            readOnly={readOnly}
                                            data-testid={`adv-pur-desc-${i}`}
                                        />
                                    </td>
                                    {/* PROFIT — auto, read-only */}
                                    <td
                                        className="border border-emerald-100 bg-emerald-50/60 px-1 py-1 font-numbers text-[12px] font-semibold text-emerald-900"
                                        data-testid={`adv-pur-profit-${i}`}
                                    >
                                        {hasInputs ? fmt(p) : ""}
                                    </td>
                                    {/* AMOUNT — manual input */}
                                    <td className="border border-emerald-100 align-top p-0">
                                        <AutoCell
                                            value={r.amount}
                                            onChange={(v) => onChange(i, "amount", v)}
                                            readOnly={readOnly}
                                            numeric
                                            data-testid={`adv-pur-amount-${i}`}
                                        />
                                    </td>
                                    <td className="border border-emerald-100 align-top p-0">
                                        <AutoCell
                                            value={r.gold_price}
                                            onChange={(v) => onChange(i, "gold_price", v)}
                                            readOnly={readOnly}
                                            numeric
                                            data-testid={`adv-pur-goldprice-${i}`}
                                        />
                                    </td>
                                    <td className="border border-emerald-100 align-top p-0">
                                        <AutoCell
                                            value={r.weight}
                                            onChange={(v) => onChange(i, "weight", v)}
                                            readOnly={readOnly}
                                            numeric
                                            data-testid={`adv-pur-weight-${i}`}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    {totals ? (
                        <tfoot className="bg-emerald-100/60 font-semibold text-emerald-900">
                            <tr>
                                <td className="border border-emerald-200 px-1 py-2 text-right text-[11px]">
                                    {t.advTotalLabel || t.dailyTotalLabel}
                                </td>
                                <td
                                    className="border border-emerald-200 px-1 py-2 font-numbers"
                                    data-testid="adv-pur-total-profit"
                                >
                                    {fmtNonZero(totals.profit)}
                                </td>
                                <td
                                    className="border border-emerald-200 px-1 py-2 font-numbers"
                                    data-testid="adv-pur-total-amount"
                                >
                                    {fmtNonZero(totals.amount)}
                                </td>
                                <td className="border border-emerald-200" />
                                <td
                                    className="border border-emerald-200 px-1 py-2 font-numbers"
                                    data-testid="adv-pur-total-weight"
                                >
                                    {fmtNonZero(totals.weight)}
                                </td>
                            </tr>
                            {/* Total Weight including Broken Weight */}
                            <tr className="bg-amber-50 text-amber-900">
                                <td className="border border-emerald-200" />
                                <td className="border border-emerald-200" />
                                <td className="border border-emerald-200" />
                                <td className="border border-emerald-200 px-1 py-2 text-right text-[11px] font-semibold">
                                    {t.advTotalWeight} (+{t.advColBrokenWeight})
                                </td>
                                <td
                                    className="border border-emerald-200 px-1 py-2 font-numbers font-bold"
                                    data-testid="adv-pur-total-weight-with-broken"
                                >
                                    {fmtNonZero(totals.weight_with_broken)}
                                </td>
                            </tr>
                        </tfoot>
                    ) : null}
                </table>
            </div>
            {!readOnly && onAddRow ? (
                <div className="border-t border-emerald-100 bg-emerald-50/30 px-3 py-2">
                    <button
                        type="button"
                        onClick={onAddRow}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-800 shadow-sm hover:bg-emerald-100"
                        data-testid="adv-pur-add-row"
                    >
                        + {t.advAddRow}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

/* ───────────────────────── Page ───────────────────────── */
export default function AdvancedDailyMovement() {
    const { t, lang } = useLang();
    const { company } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get("id");

    const [date, setDate] = useState(todayIso());
    const [sales, setSales] = useState(() =>
        Array.from({ length: SALES_ROWS }, (_, i) => emptySaleRow(i))
    );
    const [purchases, setPurchases] = useState(() =>
        Array.from({ length: PURCHASE_ROWS }, (_, i) => emptyPurchaseRow(i))
    );

    /* Section-level broken-weight (single value, locked after first entry / carry). */
    const [brokenWeight, setBrokenWeight] = useState("");
    const [brokenWeightLocked, setBrokenWeightLocked] = useState(false);

    /* Opening balances (carry-over from previous day) — always read-only. */
    const [openings, setOpenings] = useState({ cash: 0, bank: 0, transfer: 0 });
    const [carryNotice, setCarryNotice] = useState(null); // "carried" | "none"

    const [totals, setTotals] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    /* Display name for section headers: company_name for both individuals and employees
       (employees store their own name in company_name). */
    const displayName = company?.company_name || "";

    const markDirty = () => {
        setIsDirty(true);
        setTotals(null);
    };

    /* ── Carry-over from previous day on mount ── */
    useEffect(() => {
        if (editId) return; // edit-mode: load saved doc instead of carry-over
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/ledgers/daily/last-balance");
                if (cancel) return;
                if (data?.found && data?.variant === "advanced") {
                    setOpenings({
                        cash: parseFloat(data.balances?.cash) || 0,
                        bank: parseFloat(data.balances?.bank) || 0,
                        transfer: parseFloat(data.balances?.transfer) || 0,
                    });
                    const carryBroken = parseFloat(data.carry_broken_weight) || 0;
                    if (carryBroken > 0) {
                        setBrokenWeight(String(carryBroken));
                        setBrokenWeightLocked(true);
                    }
                    setCarryNotice("carried");
                } else {
                    setCarryNotice("none");
                }
            } catch {
                setCarryNotice("none");
            }
        })();
        return () => {
            cancel = true;
        };
    }, [editId]);

    /* ── Edit mode: load an existing editable advanced daily ledger ── */
    useEffect(() => {
        if (!editId) return;
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get(`/ledgers/${editId}`);
                if (cancel) return;
                if ((data?.save_status || "final") === "final") {
                    toast.error("السجل مغلق نهائياً");
                    navigate(`/history/daily/${editId}`, { replace: true });
                    return;
                }
                const dd = data?.daily_data || {};
                if (Array.isArray(dd?.sales?.rows) && dd.sales.rows.length) {
                    setSales(dd.sales.rows);
                }
                if (Array.isArray(dd?.purchases?.rows) && dd.purchases.rows.length) {
                    setPurchases(dd.purchases.rows);
                }
                if (dd?.openings) {
                    setOpenings({
                        cash: parseFloat(dd.openings.cash) || 0,
                        bank: parseFloat(dd.openings.bank) || 0,
                        transfer: parseFloat(dd.openings.transfer) || 0,
                    });
                }
                if (dd?.broken_weight) {
                    setBrokenWeight(String(dd.broken_weight));
                    setBrokenWeightLocked(true);
                }
                if (data.daily_date) setDate(data.daily_date);
                setCarryNotice(null);
                setIsDirty(false);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
                navigate("/history/daily", { replace: true });
            }
        })();
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId]);

    const updateSales = (i, field, value) => {
        setSales((rows) => {
            const next = rows.slice();
            next[i] = { ...next[i], [field]: value };
            return next;
        });
        markDirty();
    };

    const updatePurchases = (i, field, value) => {
        setPurchases((rows) => {
            const next = rows.slice();
            next[i] = { ...next[i], [field]: value };
            return next;
        });
        markDirty();
    };

    /** Append an empty row to the Sales section (#5). */
    const addSalesRow = () => {
        setSales((rows) => [...rows, emptySaleRow(rows.length)]);
        markDirty();
    };

    /** Append an empty row to the Purchases section (#5). */
    const addPurchasesRow = () => {
        setPurchases((rows) => [...rows, emptyPurchaseRow(rows.length)]);
        markDirty();
    };

    const onBrokenWeightChange = (v) => {
        if (brokenWeightLocked) return;
        setBrokenWeight(v);
        markDirty();
    };

    /* Lock the broken-weight field as soon as the user moves focus AND a value was typed. */
    const lockBrokenWeightIfFilled = () => {
        if (!brokenWeightLocked && num(brokenWeight) > 0) {
            setBrokenWeightLocked(true);
        }
    };

    const calculate = () => {
        // First, finalize the broken-weight (lock if user typed a value)
        lockBrokenWeightIfFilled();

        const hasAnything =
            sales.some(
                (r) => r.gold_price || r.mfg || r.desc || r.weight || r.cash || r.bank || r.transfer
            ) ||
            purchases.some((r) => r.weight || r.gold_price || r.desc || r.amount) ||
            num(brokenWeight) > 0;
        if (!hasAnything) {
            toast.error(t.advCalcEmpty);
            return;
        }

        // ── Sales totals
        const salesTotalWeight = sales.reduce((s, r) => s + num(r.weight), 0);
        const salesTotalCash = sales.reduce((s, r) => s + num(r.cash), 0);
        const salesTotalBank = sales.reduce((s, r) => s + num(r.bank), 0);
        const salesTotalTransfer = sales.reduce((s, r) => s + num(r.transfer), 0);
        const salesTotalProfit = sales.reduce(
            (s, r) =>
                s +
                ((num(r.gold_price) + num(r.mfg)) * num(r.weight) -
                    (num(r.cash) + num(r.bank) + num(r.transfer))),
            0
        );

        // ── Purchases totals
        const purTotalWeight = purchases.reduce((s, r) => s + num(r.weight), 0);
        const purTotalAmount = purchases.reduce((s, r) => s + num(r.amount), 0);
        const purTotalProfit = purchases.reduce(
            (s, r) => s + (num(r.weight) * num(r.gold_price) - num(r.amount)),
            0
        );
        const purBrokenWeight = num(brokenWeight);
        const weightWithBroken = purTotalWeight + purBrokenWeight;

        // ── Balances: opening + today's column total (− spending for cash only)
        const spending = purTotalAmount;
        const balanceCash = openings.cash + salesTotalCash - spending;
        const balanceBank = openings.bank + salesTotalBank;
        const balanceTransfer = openings.transfer + salesTotalTransfer;

        setTotals({
            sales: {
                weight: salesTotalWeight,
                cash: salesTotalCash,
                bank: salesTotalBank,
                transfer: salesTotalTransfer,
                profit: salesTotalProfit,
                spending,
                balance_cash: balanceCash,
                balance_bank: balanceBank,
                balance_transfer: balanceTransfer,
            },
            purchases: {
                weight: purTotalWeight,
                broken_weight: purBrokenWeight,
                weight_with_broken: weightWithBroken,
                amount: purTotalAmount,
                profit: purTotalProfit,
            },
        });
        toast.success(t.advCalcDone);
    };

    const hasAnyEntry = useMemo(() => {
        const s = sales.some(
            (r) => r.weight || r.cash || r.bank || r.transfer || r.gold_price || r.mfg || r.desc
        );
        const p = purchases.some((r) => r.weight || r.gold_price || r.desc || r.amount);
        const b = num(brokenWeight) > 0;
        return s || p || b;
    }, [sales, purchases, brokenWeight]);

    const requestSave = () => {
        if (!date) {
            toast.error(t.dailyDateRequired);
            return;
        }
        if (!hasAnyEntry) {
            toast.error(t.advEmpty);
            return;
        }
        if (!totals) calculate();
        setConfirmOpen(true);
    };

    useDirtyGuard({
        isDirty,
        save: async () => {
            requestSave();
            return false;
        },
        title: t.advSaveBtn,
    });
    useUnsavedChangesWarning(isDirty);

    const doSave = async (mode = "final") => {
        setSaving(true);
        try {
            const dailyData = {
                variant: "advanced",
                section_name: displayName,
                openings,
                broken_weight: num(brokenWeight),
                sales: {
                    rows: sales,
                    total: totals?.sales || {
                        weight: 0,
                        cash: 0,
                        bank: 0,
                        transfer: 0,
                        profit: 0,
                        spending: 0,
                        balance_cash: openings.cash,
                        balance_bank: openings.bank,
                        balance_transfer: openings.transfer,
                    },
                },
                purchases: {
                    rows: purchases,
                    total: totals?.purchases || {
                        weight: 0,
                        broken_weight: num(brokenWeight),
                        weight_with_broken: num(brokenWeight),
                        amount: 0,
                        profit: 0,
                    },
                },
            };
            const payload = {
                kind: "daily",
                client_name: `${t.advTitle} — ${date}`,
                credit_rows: [],
                debit_rows: [],
                daily_data: dailyData,
                daily_date: date,
                save_status: mode === "editable" ? "editable" : "final",
            };
            if (editId) {
                await api.put(`/ledgers/${editId}`, payload);
            } else {
                await api.post("/ledgers", payload);
            }
            setIsDirty(false);
            setConfirmOpen(false);
            toast.success(mode === "editable" ? t.ledgerSavedEditable : t.advSavedToast);
            navigate("/history/daily", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e) || t.dailySaveFail);
        } finally {
            setSaving(false);
        }
    };

    const doIgnore = () => {
        setIsDirty(false);
        setConfirmOpen(false);
        navigate(editId ? `/history/daily/${editId}` : "/history/daily");
    };

    return (
        <AppShell>
            <div
                className="mb-5 flex flex-wrap items-end justify-between gap-3"
                dir={lang === "ar" ? "rtl" : "ltr"}
            >
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700 sm:text-sm">
                        {t.advKicker}
                    </p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                        <Sparkles className="me-2 inline h-6 w-6 text-indigo-600" />
                        {t.advTitle}
                    </h1>
                </div>
                <label className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <Calendar className="h-4 w-4 text-indigo-700" />
                    <span className="text-neutral-600">{t.dailyDateLabel}</span>
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => {
                            setDate(e.target.value);
                            setIsDirty(true);
                        }}
                        className="h-8 w-36 border-0 p-0 font-numbers text-sm shadow-none focus-visible:ring-0"
                        data-testid="adv-date-input"
                    />
                </label>
            </div>

            {/* Carry-over notice */}
            {carryNotice === "carried" ? (
                <div
                    className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-sm text-amber-900"
                    data-testid="adv-carry-notice"
                >
                    <Info className="h-4 w-4" />
                    <span>{t.advCarriedFromYesterday}</span>
                </div>
            ) : carryNotice === "none" ? (
                <div
                    className="mb-4 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600"
                    data-testid="adv-no-prior-notice"
                >
                    <Info className="h-4 w-4" />
                    <span>{t.advNoPrior}</span>
                </div>
            ) : null}

            <div className="grid grid-cols-1 gap-5">
                <SalesTable
                    rows={sales}
                    onChange={updateSales}
                    t={t}
                    totals={totals?.sales}
                    openings={openings}
                    displayName={displayName}
                    onAddRow={addSalesRow}
                />
                <PurchasesTable
                    rows={purchases}
                    onChange={updatePurchases}
                    t={t}
                    totals={totals?.purchases}
                    brokenWeight={brokenWeight}
                    brokenWeightLocked={brokenWeightLocked}
                    onBrokenWeightChange={onBrokenWeightChange}
                    onBrokenWeightLock={lockBrokenWeightIfFilled}
                    displayName={displayName}
                    onAddRow={addPurchasesRow}
                />
            </div>

            <div className="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-neutral-200 bg-white/95 p-3 shadow-lg backdrop-blur sm:p-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={calculate}
                    className="h-11 gap-2 border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                    data-testid="adv-calculate-btn"
                >
                    <Calculator className="h-4 w-4" />
                    {t.advCalculateBtn}
                </Button>
                <Button
                    type="button"
                    onClick={requestSave}
                    disabled={saving}
                    className="h-11 gap-2 bg-indigo-700 hover:bg-indigo-800"
                    data-testid="adv-save-btn"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {t.advSaveBtn}
                </Button>
            </div>

            <SaveDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                saving={saving}
                onSaveFinal={() => doSave("final")}
                onSaveEditable={() => doSave("editable")}
                onIgnore={doIgnore}
                testIdPrefix="adv"
            />
        </AppShell>
    );
}
